/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosRequestConfig, AxiosResponse, Canceler, Method } from 'axios';
import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import * as Sentry from '@sentry/react';
import AuthService from './auth.service';
import { notify } from '../components/notification/notification';
import i18n from '../../i18n';

const axiosInstance = axios.create();
let cancel_req: Canceler;
let logoutCallback: (() => void) | null = null;

const BASE_URL = import.meta.env.VITE_API_URL || '';

export interface IResponseObject<T> {
  isError: boolean;
  message: string;
  data: T;
}

export { cancel_req };

export const getUrl = (url: string, params: any = {}): string => {
  if (isEmpty(params)) return url;

  Object.keys(params).forEach((key) => (params[key] == null || params[key] === '') && delete params[key]);
  return `${url}?${queryString.stringify(params)}`;
};

/**
 * Setup axios interceptor to handle token expiration
 * This should be called once when the app initializes with the logout function from AuthContext
 */

export const setupAxiosInterceptors = (onLogout: () => void) => {
  logoutCallback = onLogout;

  // Response interceptor to handle 401 errors globally
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Check if error is 401 (Unauthorized) or 403 (Forbidden) - token expired
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Call logout callback to clear auth state and redirect to login
        if (logoutCallback) {
          logoutCallback();
        }
      }
      return Promise.reject(error);
    },
  );
};

interface IMiscellaneousRequestParams {
  contentType?: string;
  isPublic?: boolean;
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  fullResponse?: boolean;
  apiVersion?: 'v1' | 'v2';
  skipNotify?: boolean;
}

/**
 * get method
 * @param request object containing axios params
 */
const get = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) =>
  commonAxios({ method: 'GET', url: getUrl(url, params), ...otherData });

/**
 * post method
 * @param request object containing axios params
 */
const post = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) =>
  commonAxios({ method: 'POST', url: getUrl(url), data: params, ...otherData });

/**
 * put method
 * @param request object containing axios params
 */
const put = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) =>
  commonAxios({ method: 'PUT', url: getUrl(url), data: params, ...otherData });

/**
 * deleteRequest method
 * @param request object containing axios params
 */
const deleteRequest = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) =>
  commonAxios({ method: 'DELETE', url: getUrl(url), data: params, ...otherData });

/**
 * patch method
 * @param request object containing axios params
 */
const patch = (url: string, params: any = {}, otherData: IMiscellaneousRequestParams = {}) =>
  commonAxios({ method: 'PATCH', url: getUrl(url), data: params, ...otherData });

interface IAxiosParams extends IMiscellaneousRequestParams {
  method: string;
  url: string;
  data?: any;
}

/**
 * commonAxios
 * @param object containing method, url, data, access token, content-type, isLogin
 */
const commonAxios = (config: IAxiosParams): Promise<any> => {
  const {
    method,
    url,
    data,
    contentType = 'application/json',
    isPublic = false,
    responseType,
    fullResponse = false,
    apiVersion = 'v1',
    skipNotify = false,
  } = config;
  const headers: Record<string, string> = {
    'Content-Type': contentType,
    'x-api-key': 'rcJIRI2jedbtmhlcPqs4ryiuaup2phMAGYebbGV',
    'Accept-Language': i18n.language || 'de',
  };

  if (!isPublic) {
    const token = AuthService.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const body = contentType === 'application/json' ? JSON.stringify(data) : data;

  const axiosInstanceParams: AxiosRequestConfig = {
    method: method as Method,
    baseURL: BASE_URL,
    url: url,
    cancelToken: new axios.CancelToken(function executor(c) {
      cancel_req = c;
    }),
    headers: headers,
    data: body,
    responseType: responseType,
  };

  return new Promise((resolve, reject) => {
    axiosInstance(axiosInstanceParams)
      .then((response: AxiosResponse<IResponseObject<any>>) => {
        const res = fullResponse ? response.data : response.data;
        if (res && res.message && !skipNotify) {
          if (res.isError) {
            notify(res.message, 'error');
          } else {
            notify(res.message, 'success');
          }
        }

        resolve(res);
      })
      .catch((error) => {
        // Capture API error in Sentry
        Sentry.captureException(error, {
          tags: {
            api_method: method,
            api_status: error.response?.status?.toString() || 'network_error',
          },
          extra: {
            apiUrl: url,
            apiData: data,
            responseMessage: error.response?.data?.message || null,
          },
        });

        if (error.response) {
          const res = error.response.data;
          const status = error.response.status;

          // check if error is having data
          if (res && status) {
            // 401 is now handled by interceptor, just show message
            if (status === 401 && res && res.message) {
              notify(res.message, 'error');
            } else if (res && res.message) {
              // if error data contains message field, add error notification
              notify(res.message, 'error');
            } else {
              notify(error.message, 'error');
            }
          }
        } else {
          notify(error.message || 'Network error', 'error');
        }
        reject(error);
      });
  });
};

export { axiosInstance };

const HttpService = {
  get,
  post,
  put,
  deleteRequest,
  patch,
};

export default HttpService;
